// @flow

import * as React from 'react';
import CONST from 'app/common/lib/Const';
import CONTAINER_NAME from 'app/common/react/components/growth/unauth/lib/loggingContainerConstants';
import i18n from 'app/common/lib/i18n';
import LoginAssistanceModal from 'app/common/react/components/growth/unauth/signup/AccountRecovery/LoginAssistanceModal';
import LoginRateLimitedModal from 'app/common/react/components/growth/unauth/signup/AccountRecovery/LoginRateLimitedModal';
import LoginWithFailedModal from 'app/common/react/components/growth/unauth/signup/AccountRecovery/LoginWithFailedModal';
import PasswordResetSentModal from 'app/common/react/components/growth/unauth/signup/AccountRecovery/PasswordResetSentModal';
import SoftBannedModal from 'app/common/react/components/growth/unauth/signup/AccountRecovery/SoftBannedModal';
import SuspendedModal from 'app/common/react/components/growth/unauth/signup/AccountRecovery/SuspendedModal';
import Toast from 'app/common/react/ui/Toast';
import type { LoginCreds } from 'app/packages/pinterest-flow-types/authentication';
import { Box } from 'gestalt';
import { type LoginIdentifier } from 'app/common/react/components/growth/unauth/signup/Login/Login';
import FacebookConnectButton, {
  FACEBOOK_IDENTIFIER,
} from 'app/common/react/components/growth/unauth/signup/FacebookConnectButton/FacebookConnectButton';
import GoogleConnectButton, {
  GOOGLE_IDENTIFIER,
} from 'app/common/react/components/growth/unauth/signup/GoogleConnectButton/GoogleConnectButton';
import { MODAL_TYPES, type ModalTypes } from 'app/www-unauth/lib/loginUtil';

export const ACCT_RECOVERY_MODAL_MAPPING: { [number]: ModalTypes } = {
  [CONST.API_LIMIT_EXCEEDED_ERROR]: MODAL_TYPES.RATE_LIMITED, // 8
  [CONST.API_EVENT_BLOCKED_ERROR]: MODAL_TYPES.RATE_LIMITED, // 9
  [CONST.USER_IN_SAFE_MODE]: MODAL_TYPES.SOFT_BANNED, // 19
  [CONST.POLICY_VIOLATION_USER]: MODAL_TYPES.SUSPENDED, // 83
  [CONST.LOGIN_BAD_PASSWORD_ASK_RESET]: MODAL_TYPES.LOGIN_ASSISTANCE, // 85
  [CONST.LOGIN_HARD_BANNED_USER]: MODAL_TYPES.SOFT_BANNED, // 88
  [CONST.RESPONSE_CODE_TOO_MANY_REQUESTS]: MODAL_TYPES.RATE_LIMITED, // 429
};

type State = {| showToast: boolean |};

type Props = {|
  email: string,
  modalType: ?ModalTypes,
  onLoginWithMfa: (LoginCreds, Object) => void,
  onLoginWithSuspended?: () => void,
  onLoginWithVoluntarilyDeactivated?: (error: Object) => void,
  updateAccountRecoveryModal: (modalType: ?ModalTypes) => void,
|};

export default class AccountRecovery extends React.Component<Props, State> {
  state = { showToast: false };

  dismissModal = () => {
    this.props.updateAccountRecoveryModal(null);
  };

  showPasswordResetSent = () =>
    this.props.updateAccountRecoveryModal(MODAL_TYPES.PASSWORD_RESET_SENT);

  showLoginWithFailed = (loginWith: LoginIdentifier) => {
    if (loginWith === FACEBOOK_IDENTIFIER) {
      this.props.updateAccountRecoveryModal(MODAL_TYPES.PREVENT_FACEBOOK_REGISTER);
    } else if (loginWith === GOOGLE_IDENTIFIER) {
      this.props.updateAccountRecoveryModal(MODAL_TYPES.PREVENT_GOOGLE_REGISTER);
    }
  };

  toggleToast = () => this.setState((state) => ({ showToast: !state.showToast }));

  renderSocialButtons = () => (
    <Box>
      <FacebookConnectButton
        buttonText={i18n._('Continue with Facebook', 'Facebook login button text')}
        container={CONTAINER_NAME.ACCOUNT_RECOVERY}
        disablePostSignupNextUrl
        onLoginWithMfa={this.props.onLoginWithMfa}
        onLoginWithSuspended={this.props.onLoginWithSuspended}
        showFallback
        preventRegister={this.showLoginWithFailed}
      />
      <Box marginTop={1}>
        <GoogleConnectButton
          container={CONTAINER_NAME.ACCOUNT_RECOVERY}
          disablePostSignupNextUrl
          onLoginWithMfa={this.props.onLoginWithMfa}
          onLoginWithSuspended={this.props.onLoginWithSuspended}
          preventRegister={this.showLoginWithFailed}
          onLoginWithVoluntarilyDeactivated={(error: Object) => {
            this.dismissModal();
            if (this.props.onLoginWithVoluntarilyDeactivated) {
              this.props.onLoginWithVoluntarilyDeactivated(error);
            }
          }}
        />
      </Box>
    </Box>
  );

  render() {
    const { email, modalType } = this.props;
    const commonProps = {
      email,
      onClose: this.dismissModal,
      onPasswordResetSent: this.showPasswordResetSent,
      socialButtons: this.renderSocialButtons(),
    };
    switch (modalType) {
      case MODAL_TYPES.SUSPENDED:
        return <SuspendedModal onClose={this.dismissModal} />;
      case MODAL_TYPES.LOGIN_ASSISTANCE:
        return <LoginAssistanceModal {...commonProps} />;
      case MODAL_TYPES.RATE_LIMITED:
        return <LoginRateLimitedModal {...commonProps} />;
      case MODAL_TYPES.SOFT_BANNED:
        return <SoftBannedModal {...commonProps} />;
      case MODAL_TYPES.PREVENT_FACEBOOK_REGISTER:
        return <LoginWithFailedModal {...commonProps} loginWith={FACEBOOK_IDENTIFIER} />;
      case MODAL_TYPES.PREVENT_GOOGLE_REGISTER:
        return <LoginWithFailedModal {...commonProps} loginWith={GOOGLE_IDENTIFIER} />;
      case MODAL_TYPES.PASSWORD_RESET_SENT:
        return (
          <React.Fragment>
            <PasswordResetSentModal {...commonProps} onPasswordResetSent={this.toggleToast} />
            {this.state.showToast && (
              <Box
                fit
                dangerouslySetInlineStyle={{
                  __style: {
                    bottom: '50%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                  },
                }}
                paddingX={1}
                position="fixed"
              >
                <Toast onHide={this.toggleToast} text={i18n._('Email sent!')} duration={3500} />
              </Box>
            )}
          </React.Fragment>
        );
      default:
        return null;
    }
  }
}
